/**
 * Desktop Web Theme - Bold Dashboard Style
 * Enterprise analytics aesthetic with strong colors and data-dense layouts
 */

export const DesktopTheme = {
  // Color palette - Dark sidebar with vibrant accents
  colors: {
    // Backgrounds
    sidebarBg: '#0f172a',         // Dark slate
    sidebarHover: '#1e293b',      // Slightly lighter slate
    sidebarActive: '#334155',     // Active item background
    contentBg: '#f8fafc',         // Light gray content area
    cardBg: '#ffffff',            // White cards
    headerBg: '#ffffff',          // White header

    // Primary brand colors (keeping ESG green heritage)
    primary: '#059669',           // Emerald green
    primaryHover: '#047857',      // Darker emerald
    primaryLight: '#d1fae5',      // Light emerald for highlights

    // Pillar-specific colors
    environmental: '#10b981',     // Emerald
    environmentalBg: '#ecfdf5',   // Light emerald bg
    social: '#3b82f6',           // Blue
    socialBg: '#eff6ff',         // Light blue bg
    governance: '#8b5cf6',       // Purple
    governanceBg: '#f5f3ff',     // Light purple bg

    // Status colors
    success: '#22c55e',          // Green
    warning: '#f59e0b',          // Amber
    error: '#ef4444',            // Red
    info: '#0ea5e9',             // Sky blue

    // Text colors
    textPrimary: '#0f172a',      // Dark text
    textSecondary: '#64748b',    // Gray text
    textMuted: '#94a3b8',        // Light gray text
    textOnDark: '#f1f5f9',       // Light text on dark bg
    textOnDarkMuted: '#94a3b8',  // Muted text on dark bg

    // Sidebar text colors
    sidebarText: '#ffffff',      // White text on sidebar
    sidebarTextMuted: '#cbd5e1', // Lighter muted text on sidebar

    // Borders
    border: '#e2e8f0',           // Light border
    borderDark: '#334155',       // Dark border for sidebar

    // Score gradient colors (for progress rings)
    scoreHigh: '#22c55e',        // 80-100
    scoreMedium: '#f59e0b',      // 50-79
    scoreLow: '#ef4444',         // 0-49
  },

  // Typography
  typography: {
    fontFamily: {
      heading: 'LatoBlack',
      body: 'LatoRegular',
    },
    fontSize: {
      h1: 32,
      h2: 24,
      h3: 20,
      h4: 18,
      body: 14,
      bodyLarge: 16,
      small: 12,
      tiny: 10,
    },
    fontWeight: {
      regular: '400',
      medium: '500',
      semibold: '600',
      bold: '700',
    },
    lineHeight: {
      tight: 1.25,
      normal: 1.5,
      relaxed: 1.75,
    },
  },

  // Spacing scale
  spacing: {
    xs: 4,
    sm: 8,
    md: 16,
    lg: 24,
    xl: 32,
    xxl: 48,
  },

  // Layout dimensions
  layout: {
    sidebarWidth: 260,
    sidebarCollapsedWidth: 72,
    headerHeight: 64,
    maxContentWidth: 1400,
    cardGap: 24,
    pageGutter: 32,
  },

  // Border radius
  borderRadius: {
    sm: 4,
    md: 8,
    lg: 12,
    xl: 16,
    full: 9999,
  },

  // Shadows
  shadows: {
    sm: {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 1 },
      shadowOpacity: 0.05,
      shadowRadius: 2,
      elevation: 1,
    },
    md: {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.1,
      shadowRadius: 6,
      elevation: 3,
    },
    lg: {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 10 },
      shadowOpacity: 0.15,
      shadowRadius: 15,
      elevation: 5,
    },
    card: {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.08,
      shadowRadius: 8,
      elevation: 2,
    },
  },

  // Breakpoints
  breakpoints: {
    mobile: 0,
    tablet: 768,
    desktop: 1024,
    wide: 1440,
  },

  // Transitions (for web CSS)
  transitions: {
    fast: '150ms ease',
    normal: '200ms ease',
    slow: '300ms ease',
  },
};

// Helper function to get score color based on value
export const getScoreColor = (score) => {
  if (score >= 80) return DesktopTheme.colors.scoreHigh;
  if (score >= 50) return DesktopTheme.colors.scoreMedium;
  return DesktopTheme.colors.scoreLow;
};

// Helper function to get pillar color
export const getPillarColor = (pillar) => {
  switch (pillar?.toLowerCase()) {
    case 'environmental':
      return DesktopTheme.colors.environmental;
    case 'social':
      return DesktopTheme.colors.social;
    case 'governance':
      return DesktopTheme.colors.governance;
    default:
      return DesktopTheme.colors.primary;
  }
};

// Helper function to get pillar background color
export const getPillarBgColor = (pillar) => {
  switch (pillar?.toLowerCase()) {
    case 'environmental':
      return DesktopTheme.colors.environmentalBg;
    case 'social':
      return DesktopTheme.colors.socialBg;
    case 'governance':
      return DesktopTheme.colors.governanceBg;
    default:
      return DesktopTheme.colors.cardBg;
  }
};

export default DesktopTheme;
